import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useOnboarding } from "@/context/OnboardingContext";
import { useLang } from "@/context/LanguageContext";
import LanguagePill from "@/components/LanguagePill";
import OnboardingExit from "@/components/OnboardingExit";

const DRAFT_KEY = "vedari_life_letter_draft";

export default function OnboardingLifeLetter() {
  const navigate = useNavigate();
  const { data, update } = useOnboarding();
  const { t } = useLang();
  const initial = (() => {
    try {
      const stored = JSON.parse(localStorage.getItem(DRAFT_KEY) || "null");
      if (stored) return stored;
    } catch {}
    return data.life_letter || {};
  })();
  const [q1, setQ1] = useState(initial.life_situation || "");
  const [q2, setQ2] = useState(initial.hardest_thing || "");
  const [q3, setQ3] = useState(initial.aspiration || "");
  const [saving, setSaving] = useState(false);

  // Persist draft to localStorage as user types (debounced)
  useEffect(() => {
    const t = setTimeout(() => {
      try {
        localStorage.setItem(DRAFT_KEY, JSON.stringify({ life_situation: q1, hardest_thing: q2, aspiration: q3 }));
      } catch {}
    }, 400);
    return () => clearTimeout(t);
  }, [q1, q2, q3]);

  const persistAndContinue = async (skip = false) => {
    setSaving(true);
    const payload = skip
      ? { life_situation: "", hardest_thing: "", aspiration: "" }
      : { life_situation: q1, hardest_thing: q2, aspiration: q3 };
    try {
      await api.put("/personalization/life-letter", payload);
      update({ life_letter: payload });
      try { localStorage.removeItem(DRAFT_KEY); } catch {}
    } catch {}
    navigate("/onboarding/birth");
  };

  return (
    <div className="min-h-screen px-6 py-10 max-w-md mx-auto" style={{ backgroundColor: "var(--vedari-cream)", color: "var(--vedari-ink)" }}>
      <div className="flex items-center justify-between mb-6 gap-3">
        <div className="flex items-center gap-2 flex-1">
          {[1,2,3,4,5].map(i => <span key={i} className="h-[2px] flex-1 rounded-full" style={{ backgroundColor: i <= 4 ? "var(--vedari-gold)" : "var(--vedari-divider)" }} />)}
        </div>
        <LanguagePill persist />
        <OnboardingExit />
      </div>

      <h1 className="font-display text-[34px] leading-tight" data-testid="life-letter-title">
        {t("ob.letter.title")}
      </h1>
      <p className="text-body mt-3 leading-relaxed" style={{ color: "var(--vedari-ink-soft)" }}>
        {t("ob.letter.sub")}
      </p>

      <div className="space-y-8 mt-8">
        <Field
          label={t("ob.letter.q1_label")}
          helper={t("ob.letter.q1_helper")}
          placeholder={t("ob.letter.ph")}
          value={q1} onChange={setQ1} max={500} rows={5} testid="ll-q1"
        />
        <Field
          label={t("ob.letter.q2_label")}
          helper={t("ob.letter.q2_helper")}
          placeholder={t("ob.letter.ph")}
          value={q2} onChange={setQ2} max={300} rows={4} testid="ll-q2"
        />
        <Field
          label={t("ob.letter.q3_label")}
          helper={t("ob.letter.q3_helper")}
          placeholder={t("ob.letter.ph")}
          value={q3} onChange={setQ3} max={300} rows={4} testid="ll-q3"
        />
      </div>

      <p className="text-caption mt-8 italic font-serif leading-relaxed" style={{ color: "var(--vedari-ink-soft)" }}>
        {t("ob.letter.privacy")}
      </p>

      <button onClick={() => persistAndContinue(false)} disabled={saving} className="vedari-btn-primary w-full mt-8" data-testid="life-letter-continue-btn">
        {saving ? t("ob.letter.saving") : t("common.continue")}
      </button>
      <button onClick={() => persistAndContinue(true)} disabled={saving} className="vedari-btn-tertiary w-full mt-2" data-testid="life-letter-skip-btn">
        {t("ob.letter.skip")}
      </button>
    </div>
  );
}

function Field({ label, helper, value, onChange, max, rows, testid, placeholder }) {
  return (
    <div>
      <label className="block font-serif text-h3 mb-1" style={{ color: "var(--vedari-ink)" }}>{label}</label>
      <p className="text-body-sm italic font-serif mb-3" style={{ color: "var(--vedari-ink-soft)" }}>{helper}</p>
      <textarea
        className="vedari-input resize-none"
        rows={rows}
        maxLength={max}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
      />
      <p className="text-caption text-right mt-1" style={{ color: "var(--vedari-ink-soft)" }}>{value.length} / {max}</p>
    </div>
  );
}
